import React from "react";
import Fragment from "react-dot-fragment";
import _ from "lodash";
import styled from "styled-components";

import PerformanceComponent from "./performance";
import Chart from "./chart";
import Auth from "routes/Auth";

import Teams from "api/Teams";

import { getRequest } from "api/ApiService";
import { dedupedGet } from "api/InFlightDedup";
import moment from "moment";
import get from "lodash/get";

import PeerEvaluation from "../peer-evaluation";
import IconLock from "../../containers/assets/icon-lock.svg";

import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend,
} from 'recharts';
import cn from 'classnames';

// profile components
const Profile = {};

Profile.Wrapper = styled.section`
  width: 100%;
  padding: 20px 40px !important;
  max-width: 1180px;
  background-color: hsla(0, 0%, 98%, 1);
  box-sizing: content-box;
`;

Profile.CardWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 20px;
`;

Profile.Title = styled.div`
  font-family: Lato;
  font-size: 21px;
  font-weight: 600;
  color: lightgray;

  ${p =>
    p.marginTop &&
    `
    margin-top: ${p.marginTop};
  `}
`;

// evaluation form components
const EvaluationForm = {};

EvaluationForm.Wrapper = styled.div`
  padding: 30px 0;
  display: flex;
  align-items: center;
`;

EvaluationForm.Circle = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 30px;
  margin-right: 15px;

  ${p =>
    p.background &&
    `
    background: ${p.background};
  `}
`;

EvaluationForm.Title = styled.div`
  font-family: Lato;
  font-size: 24px;
  font-weight: 700;
  color: #5f5f5f;
`;

// performance components
const Performance = {};

Performance.Wrapper = styled.div`
  width: 100%;
  margin-top: 20px;
  padding: 20px;
  background-color: hsla(0, 0%, 100%, 1);
  border: 1px solid hsla(213, 100%, 12%, 0.15);
  border-radius: 8px;
`;

Performance.Title = styled.div`
  font-family: "Mukta Vaani", sans-serif;
  font-weight: 700;
  font-size: 21px;
  line-height: 28px;
  color: hsla(213, 100%, 12%, 0.94);
`;

// -----------------------------------------------------------------
// Peer-rating summary + teammates-said grid.
// Replaces the old "Performance Summary" block. Hardcoded values for
// now — will be wired to real data (average, median, comment counts,
// feedback strings) in a follow-up once the BE endpoints are ready.
// -----------------------------------------------------------------
const Summary = {};

Summary.Grid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  gap: 20px;
  margin-top: 20px;
  font-family: 'Mukta Vaani', sans-serif;
`;

Summary.Card = styled.div`
  background: #ffffff;
  border: 1px solid hsla(213, 100%, 12%, 0.15);
  border-radius: 12px;
  padding: 28px 32px;
`;

Summary.SectionTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: hsla(213, 100%, 12%, 0.94);
  margin-bottom: 24px;
`;

Summary.TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

Summary.TitleRowTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: hsla(213, 100%, 12%, 0.94);
`;

Summary.Toggle = styled.div`
  display: inline-flex;
  padding: 4px;
  background: hsla(213, 100%, 12%, 0.06);
  border-radius: 8px;
  gap: 2px;
`;

Summary.ToggleButton = styled.button`
  padding: 6px 14px;
  border: 0;
  border-radius: 6px;
  font-family: 'Mukta Vaani', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.active ? '#ffffff' : 'transparent'};
  color: ${p => p.active
    ? 'hsla(213, 100%, 12%, 0.94)'
    : 'hsla(213, 100%, 12%, 0.6)'};
  box-shadow: ${p => p.active
    ? '0 1px 2px rgba(0, 0, 0, 0.08)'
    : 'none'};

  &:hover {
    color: hsla(213, 100%, 12%, 0.94);
  }
`;

Summary.RatingLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: hsla(213, 100%, 12%, 0.6);
  text-transform: uppercase;
  margin-bottom: 4px;
`;

Summary.RatingValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 14px;
`;

Summary.RatingValue = styled.span`
  font-size: 56px;
  font-weight: 700;
  line-height: 1;
  color: #2AB3AF;
`;

Summary.RatingDenom = styled.span`
  font-size: 22px;
  font-weight: 500;
  color: hsla(213, 100%, 12%, 0.5);
`;

Summary.DeltaChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: hsla(151, 55%, 92%, 1);
  color: hsl(151, 60%, 30%);
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 18px;

  &::before {
    content: '▲';
    font-size: 10px;
  }
`;

Summary.Blurb = styled.p`
  font-size: 15px;
  line-height: 22px;
  color: hsla(213, 100%, 12%, 0.78);
  margin: 0 0 20px;
`;

Summary.BreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 15px;
  color: hsla(213, 100%, 12%, 0.86);

  &:not(:last-child) {
    border-bottom: 1px solid hsla(213, 100%, 12%, 0.06);
  }
`;

Summary.BreakdownLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${p => p.dotColor || '#c4c4c4'};
  }
`;

Summary.BreakdownValue = styled.span`
  font-weight: 600;
`;

Summary.StatsRow = styled.div`
  display: flex;
  gap: 40px;
  margin: 24px 0;
`;

Summary.Stat = styled.div`
  display: flex;
  flex-direction: column;
`;

Summary.StatValue = styled.span`
  font-size: 32px;
  font-weight: 700;
  color: hsla(213, 100%, 12%, 0.94);
  line-height: 1;
`;

Summary.StatLabel = styled.span`
  font-size: 14px;
  color: hsla(213, 100%, 12%, 0.6);
  margin-top: 4px;
`;

Summary.FeedbackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  background: #2AB3AF;
  color: #ffffff;
  border: 1px solid #2AB3AF;
  border-radius: 6px;
  font-family: 'Mukta Vaani', sans-serif;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;

  &:hover { background: #23918d; border-color: #23918d; }

  &:disabled {
    background: hsla(213, 100%, 12%, 0.06);
    color: hsla(213, 100%, 12%, 0.4);
    border-color: hsla(213, 100%, 12%, 0.1);
    cursor: default;
  }

  &::before {
    content: '●';
    font-size: 10px;
  }
`;

// -----------------------------------------------------------------
// Feedback modal — appears when "View feedback" is clicked. Two-tab
// (Final / Initial) with two columns per tab (What they appreciate /
// Room to grow). Hardcoded quotes for now — will be wired to a BE
// endpoint in a follow-up.
// -----------------------------------------------------------------
const Feedback = {};

Feedback.Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1500;
  padding: 24px;
`;

Feedback.Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 22px 70px rgba(0, 0, 0, 0.28);
  width: 100%;
  max-width: 1120px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Mukta Vaani', sans-serif;
`;

Feedback.Header = styled.div`
  padding: 24px 32px 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid hsla(213, 100%, 12%, 0.08);
`;

Feedback.HeaderText = styled.div`
  flex: 1 1 auto;
`;

Feedback.Title = styled.h2`
  font-weight: 700;
  font-size: 22px;
  line-height: 30px;
  color: hsla(213, 100%, 12%, 0.94);
  margin: 0 0 6px;
`;

Feedback.Subtitle = styled.p`
  font-size: 15px;
  line-height: 22px;
  color: hsla(213, 100%, 12%, 0.6);
  margin: 0;
`;

Feedback.Close = styled.button`
  flex: 0 0 auto;
  background: #ffffff;
  border: 1px solid hsla(213, 100%, 12%, 0.15);
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: hsla(213, 100%, 12%, 0.6);
  font-size: 20px;
  line-height: 1;

  &:hover { background: hsla(213, 100%, 12%, 0.04); }
`;

Feedback.TabRow = styled.div`
  padding: 16px 32px 0;
  display: flex;
  gap: 4px;
`;

Feedback.Tab = styled.button`
  padding: 10px 20px;
  background: ${p => p.active ? '#ffffff' : 'hsla(213, 100%, 12%, 0.04)'};
  border: 1px solid ${p => p.active
    ? 'hsla(213, 100%, 12%, 0.15)'
    : 'transparent'};
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  font-family: 'Mukta Vaani', sans-serif;
  font-weight: 600;
  font-size: 15px;
  color: ${p => p.active
    ? 'hsla(213, 100%, 12%, 0.94)'
    : 'hsla(213, 100%, 12%, 0.6)'};
  cursor: pointer;
`;

Feedback.Body = styled.div`
  padding: 24px 32px 32px;
  overflow-y: auto;
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
`;

Feedback.ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
`;

Feedback.ColumnIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${p => p.background || '#e5e5e5'};
  color: ${p => p.color || '#ffffff'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
`;

Feedback.ColumnLabel = styled.div`
  display: flex;
  flex-direction: column;
`;

Feedback.ColumnTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: hsla(213, 100%, 12%, 0.94);
  line-height: 22px;
`;

Feedback.ColumnSubtitle = styled.div`
  font-size: 14px;
  color: hsla(213, 100%, 12%, 0.6);
  line-height: 20px;
`;

Feedback.QuoteCard = styled.div`
  background: #ffffff;
  border: 1px solid hsla(213, 100%, 12%, 0.08);
  border-left: 3px solid ${p => p.accent || '#e5e5e5'};
  border-radius: 8px;
  padding: 16px 18px;
  margin-bottom: 12px;
`;

Feedback.QuoteText = styled.div`
  font-size: 15px;
  line-height: 22px;
  color: hsla(213, 100%, 12%, 0.86);
`;

Feedback.QuoteAttrib = styled.div`
  font-size: 13px;
  color: hsla(213, 100%, 12%, 0.55);
  font-weight: 500;
`;

Performance.Menu = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 16px;
  border-bottom: 1px solid hsla(213, 100%, 12%, 0.15);
`;

const LockScreen = {};
LockScreen.Wrapper = styled.div`
  height: 550px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 20px;
  padding: 20px;
  background-color: hsla(0, 0%, 100%, 1);
  border: 1px solid hsla(213, 100%, 12%, 0.15);
  border-radius: 8px;
`;
LockScreen.Text = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #3A507B;
  margin-top: 18px;
`;

// table components

const Table = {};

Table.Wrapper = styled.div`
  background: #fff;
  margin-top: 20px;
  width: 100%;
  max-height: 450px;
  position: relative;
  z-index: 1;
`;

Table.Row = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px;

  ${p =>
    p.background &&
    `
    background: ${p.background};
  `}
  img {
    cursor: pointer;
  }
`;

Table.Column = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  font-family: "Mukta Vaani", sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  color: hsla(213, 100%, 12%, 0.94);
  padding: 15px 20px;

  ${p =>
    p.align &&
    `
    text-align: ${p.align};
  `}
  ${p =>
    p.width &&
    `
    flex: 0 0 ${p.width};
  `}

  ${p =>
    p.isValue &&
    `
    font-weight: 500;
  `}
`;

// charts components
const Charts = {};

Charts.Wrapper = styled.div`
  background: #fff;
  margin-top: 20px;
  width: 100%;
  z-index: 1;

  ${p =>
    p.setMaxHeight &&
    `
    max-height: 450px;
  `}
`;

Charts.Teams = {};

Charts.Teams.Wrapper = styled.div`
  display: flex;
  padding-top: 30px;
  padding-bottom: 20px;
  color: #36507e;
  font-weight: 600;
  font-size: 15px;
`;

Charts.Teams.Row = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  ${p =>
    p.margin &&
    `
    margin: ${p.margin};
  `}
`;

Charts.Teams.TeamColor = styled.div`
  height: 10px;
  width: 40px;
  margin-right: 10px;

  ${p =>
    p.background &&
    `
    background: ${p.background};
  `}
`;

Charts.Teams.TeamName = styled.div`
  font-size: 14px;
  color: #36507e;
`;

Charts.Chart = styled.div`
  width: 100%;
`;

const SkillsAssessment = {};

SkillsAssessment.Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  margin-top: 20px;
  gap: 20px;
`;

SkillsAssessment.InfoTitle = styled.div`
  font-family: "Mukta Vaani", sans-serif;
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
  margin-top: 20px;
  color: rgba(0, 27, 59, 0.94);
`;

SkillsAssessment.InfoBody = styled.div`
  font-family: "Mukta Vaani", sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  margin-top: 8px;
  color: rgba(0, 27, 59, 0.94);
`;

SkillsAssessment.TagWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
`;

SkillsAssessment.Tag = styled.div`
  font-family: "Mukta Vaani", sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  padding: 1px 8px;
  border-radius: 2px;
  border: 1px solid rgba(0, 27, 59, 0.15);
  color: #5c91f5;
`;

SkillsAssessment.TagTitle = styled.div`
  font-family: "Mukta Vaani", sans-serif;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: rgba(0, 27, 59, 0.6);
`;

SkillsAssessment.SkillsMap = styled.div``;

SkillsAssessment.SkillsMapTitle = styled.div`
  font-family: "Mukta Vaani", sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
`;

SkillsAssessment.SkillsMapBody = styled.div`
  font-family: "Mukta Vaani", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  margin-top: 8px;
`;

SkillsAssessment.MintSection = styled.div`
  margin-top: 16px;
  padding: 20px;
  border: 1px solid #6fd1d9;
  background: rgba(82, 197, 205, 0.5);
  border-radius: 4px;
`;

SkillsAssessment.OrangeSection = styled.div`
  margin-top: 16px;
  padding: 20px;
  border: 1px solid #fd9595;
  background: rgba(255, 153, 102, 0.5);
  border-radius: 4px;
`;

SkillsAssessment.ColorSectionTitle = styled.div`
  font-family: "Mukta Vaani", sans-serif;
  font-weight: 700;
  font-size: 21px;
  line-height: 28px;
  color: #2a4169;
`;

SkillsAssessment.ColorSectionBody = styled.div`
  margin-top: 16px;
  font-family: "Mukta Vaani", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
`;

SkillsAssessment.ColorSectionTags = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
`;

SkillsAssessment.ColorSectionTag = styled.div`
  padding: 1px 8px;
  background: #fff;
  border: 1.5px solid #2a4169;
  border-radius: 4px;
  font-family: "Mukta Vaani", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 23px;
  color: #2a4169;
`;

SkillsAssessment.ChartDescription = styled.div`
  max-width: 560px;
  margin-top: 20px;
  font-family: "Mukta Vaani", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);

  b {
    font-weight: 700;
  }
`;

SkillsAssessment.RadarChartWrapper = styled.div`
  background: rgba(0, 27, 43, 0.02);

  .recharts-legend-item-text {
    font-family: "Mukta Vaani", sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: rgba(0, 27, 59, 0.94);
  }

  .recharts-default-legend {
    display: flex;
    flex-direction: row-reverse;
    justify-content: center;
    gap: 20px;
  }

  .recharts-legend-item {
    display: block !important;
    margin: 0 !important;

    svg {
      margin-right: 12px !important;

      line {
        stroke-width: 2;
      }
    }
  }

  .recharts-polar-angle-axis-tick-value {
    font-family: "Mukta Vaani", sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    fill: rgba(0, 27, 59, 0.6);

    &:hover, &.recharts-polar-angle-axis-tick-value-selected {
      font-weight: 700;
      fill: rgba(0, 27, 59, 0.94);
      cursor: pointer;
    }
  }

  .recharts-polar-angle-axis-ticks {
    transform: translateY(5px);
  }
`;

export default class PeerReview extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      performance: [
        { id: 1, name: "Peer Rating", isActive: true },
      ],
      surveys: [],
      reports: {},
      grading: {},
      teamGrade: 0,
      currentScore: {},
      userGrade: {},
      skillsAssessmentCalculated: false,
      skillsAssessmentRating: [],
      skillsAssessmentTooltip: null,
      skillsAssessmentDefinitions: {},
      peerScoreValue: null,
      showPerformanceSummary: false,
      // Map of surveyId → { average_user_score, team_average_score,
      // team_median_score } from /v2/forms/surveys/<id>/user-score/<uid>/.
      // Fed by fetchUserScores() in componentDidMount once we know
      // which surveys are finished.
      userScores: {},
      // Which finished peer-eval survey is currently displayed in the
      // peer-rating + teammates cards + feedback modal. Defaults to
      // Final if present, else Initial. Toggled via any of the
      // Initial/Final buttons — all three surfaces stay in sync.
      selectedSurveyId: null,
      // Map of surveyId → /v2/insights/mine/<id>/ response body.
      // Drives the "What your teammates said" blurb (overall_summary)
      // and the feedback modal columns (strengths / weaknesses
      // evidence). null value = fetch returned nothing / errored.
      insights: {},
      feedbackModalOpen: false,
    };
  }

  openFeedbackModal = () => this.setState({ feedbackModalOpen: true });
  closeFeedbackModal = () => this.setState({ feedbackModalOpen: false });

  getSkillsAssessmentDefinitions = survey => {
    let definitions = [];

    for (const question of survey.questions) {
      definitions = definitions.concat(question.statements);
    }

    return definitions
      .map(d => d.student_category || null)
      .filter(d => !!d)
      .reduce((obj, item) => {
        return {
          ...obj,
          [item.name]: item.definition,
        };
      }, {});
  }

  // Poll sessionStorage every 100ms up to 5s waiting for App.js's
  // general-info fetch to populate the course_id. Prevents a
  // race-condition GET to `?course=0` when a student navigates
  // directly to /peer-review before the initial data load resolves.
  _waitForCourseId = () => new Promise(resolve => {
    let elapsed = 0;
    const check = () => {
      const id = Auth.getCourseId();
      if (id) return resolve(id);
      elapsed += 100;
      if (elapsed >= 5000) return resolve(0);
      setTimeout(check, 100);
    };
    check();
  });

  // Fires GET /v2/forms/surveys/<id>/user-score/<userId>/ for every
  // finished Initial + Final peer-eval survey. Response fields used:
  //   average_user_score, team_average_score, team_median_score.
  // Populates state.userScores keyed by survey id and picks a
  // sensible default for selectedSurveyId (Final if we have it, else
  // Initial). Silently skips any survey whose fetch fails.
  fetchUserScores = (finishedSurveys) => {
    const peerEvalSurveys = (finishedSurveys || []).filter(
      s => s && (s.stage === 'initial' || s.stage === 'final')
    );
    if (peerEvalSurveys.length === 0) return;
    const userId = Auth.getUserId();
    if (!userId) return;
    const apiUrl = process.env.REACT_APP_API_URL;
    Promise.all(
      peerEvalSurveys.map(s =>
        getRequest(`${apiUrl}v2/forms/surveys/${s.id}/user-score/${userId}/`)
          .then(res => ({ id: s.id, body: (res && res.body) || null }))
          .catch(() => ({ id: s.id, body: null }))
      )
    ).then(results => {
      const userScores = {};
      results.forEach(r => {
        if (r.body) userScores[r.id] = r.body;
      });
      const finalSurvey = peerEvalSurveys.find(s => s.stage === 'final');
      const initialSurvey = peerEvalSurveys.find(s => s.stage === 'initial');
      const selectedSurveyId = (finalSurvey && userScores[finalSurvey.id])
        ? finalSurvey.id
        : (initialSurvey && userScores[initialSurvey.id])
          ? initialSurvey.id
          : null;
      this.setState({ userScores, selectedSurveyId });
    });
  };

  selectSurveyById = (surveyId) => {
    this.setState({ selectedSurveyId: surveyId });
  };

  // Fires GET /v2/insights/mine/<surveyId>/ for each finished Initial
  // + Final peer-eval. Response has insight_json.overall_summary
  // (drives the teammates-said blurb) plus strengths.skills[] and
  // weaknesses.skills[] with evidence arrays (drives the feedback
  // modal columns). Silently stores null for any survey whose fetch
  // fails or 404s — the UI treats that as "no insights released yet".
  fetchInsights = (finishedSurveys) => {
    const peerEvalSurveys = (finishedSurveys || []).filter(
      s => s && (s.stage === 'initial' || s.stage === 'final')
    );
    if (peerEvalSurveys.length === 0) return;
    const apiUrl = process.env.REACT_APP_API_URL;
    Promise.all(
      peerEvalSurveys.map(s =>
        getRequest(`${apiUrl}v2/insights/mine/${s.id}/`)
          .then(res => ({ id: s.id, body: (res && res.body) || null }))
          .catch(() => ({ id: s.id, body: null }))
      )
    ).then(results => {
      const insights = {};
      results.forEach(r => { insights[r.id] = r.body; });
      this.setState({ insights });
    });
  };

  async componentDidMount() {
    const courseId = await this._waitForCourseId();
    if (!courseId) return;
    const surveysRaw = await dedupedGet(
      `${process.env.REACT_APP_API_URL}v2/forms/surveys/?course=${courseId}`
    );
    const surveys = surveysRaw.body.results;

    const finishedSurveys =
      surveys && surveys.length > 0
        ? surveys.filter(i => this.isSurveyFinished(i))
        : [];
    const lastSurveyId =
      finishedSurveys.length > 0
        ? finishedSurveys[finishedSurveys.length - 1].id
        : 0;

    // Fire the user-score fetches for finished Initial + Final peer
    // eval surveys in parallel. Each response provides the average
    // user score plus team average / median — displayed in the
    // "Your peer rating" card. Fire-and-forget; setState handles the
    // late arrival, and if any request fails we just render "—" for
    // that survey.
    this.fetchUserScores(finishedSurveys);
    // Same idea for insights: overall_summary + strengths/weaknesses
    // evidence per survey. Drives the "What your teammates said"
    // card + feedback modal.
    this.fetchInsights(finishedSurveys);

    if (surveys.length > 0) {
      let skillsAssessmentCalculated = false;
      const skillsAssessmentRating = [];
      const performance = [
        ...this.state.performance,
      ];
      let skillsAssessmentDefinitions = {};

      const initialSurvey = surveys.find(i => i.stage === 'initial');
      let initialSurveyExists = false;
      if (initialSurvey && initialSurvey.questions) {
        const skillsQuestions = initialSurvey.questions.filter(i => ['other', 'skills'].includes(i.category) && i.answer_type === 'freeform');
        if (skillsQuestions.length > 0) {
          initialSurveyExists = true;
          skillsAssessmentDefinitions = this.getSkillsAssessmentDefinitions(initialSurvey);

          try {
            const { body } = await getRequest(
              `${process.env.REACT_APP_API_URL}v2/forms/skills-assessment/${initialSurvey.id}/student/${Auth.getUserId()}/`
            );
            if (body) {
              skillsAssessmentRating.push(body);
              skillsAssessmentCalculated = Object.values(body).some(i => i > 0);
            }
          } catch (e) {
            initialSurveyExists = false;
          }
        }
      }

      if (initialSurveyExists) {
        const secondSurvey = surveys.find(i => i.stage === 'second');
        if (secondSurvey && secondSurvey.questions) {
          const skillsQuestions = secondSurvey.questions.filter(i => ['other', 'skills'].includes(i.category) && i.answer_type === 'freeform');
          if (skillsQuestions.length > 0) {
            skillsAssessmentDefinitions = this.getSkillsAssessmentDefinitions(secondSurvey);

            try {
              const { body } = await getRequest(
                `${process.env.REACT_APP_API_URL}v2/forms/skills-assessment/${secondSurvey.id}/student/${Auth.getUserId()}/`
              );
              if (body && Object.values(body).some(i => i > 0)) {
                skillsAssessmentRating.push(body);
                if (!skillsAssessmentCalculated) {
                  skillsAssessmentCalculated = Object.values(body).some(i => i > 0);
                }
              }
            } catch (e) {
              // do nothing
            }
          }
        }

        const finalSurvey = surveys.find(i => i.stage === 'final');
        if (finalSurvey && finalSurvey.questions) {
          const skillsQuestions = finalSurvey.questions.filter(i => ['other', 'skills'].includes(i.category) && i.answer_type === 'freeform');
          if (skillsQuestions.length > 0) {
            skillsAssessmentDefinitions = this.getSkillsAssessmentDefinitions(finalSurvey);

            try {
              const { body } = await getRequest(
                `${process.env.REACT_APP_API_URL}v2/forms/skills-assessment/${finalSurvey.id}/student/${Auth.getUserId()}/`
              );
              if (body && Object.values(body).some(i => i > 0)) {
                skillsAssessmentRating.push(body);
                if (!skillsAssessmentCalculated) {
                  skillsAssessmentCalculated = Object.values(body).some(i => i > 0);
                }
              }
            } catch (e) {
              // do nothing
            }
          }
        }
      }

      if (initialSurveyExists) {
        performance.push({ id: 3, name: 'Skills Assessment', isActive: false });
      }

      const formsFeedbacksRaw = await dedupedGet(
        `${process.env.REACT_APP_API_URL}v2/forms/feedbacks/`
      );
      const formsFeedbacks = formsFeedbacksRaw.body.results;
      let allFeedbacksCompleted = true;
      finishedSurveys.forEach(finishedSurvey => {
        const notCompletedSurveyFormsFeedbacks = formsFeedbacks.filter(formsFeedback => formsFeedback.survey === finishedSurvey.id && formsFeedback.completed !== true);
        if (notCompletedSurveyFormsFeedbacks.length) {
          allFeedbacksCompleted = false;
          return;
        }
      });

      this.setState({
        performance,
        skillsAssessmentCalculated,
        skillsAssessmentRating,
        skillsAssessmentDefinitions,
        showPerformanceSummary: allFeedbacksCompleted || finishedSurveys.length
      });
    }

    if (lastSurveyId > 0) {
      try {
        const { body: currentScore } = await getRequest(
          `${process.env.REACT_APP_API_URL
          }v2/forms/score/${Auth.getUserId()}/${lastSurveyId}/`
        );

        this.setState({
          currentScore
        });
      } catch (e) {
        const errorDetail = get(e, 'response.body.detail', null);
        if (errorDetail === 'Not found.') {
          this.setState({
            currentScore: {},
          });
        }
      }
    }

    const { rounds } = this.props;
    const maxRoundNumber = Math.max.apply(
      null,
      rounds.map(round => round.number)
    );
    const latestCompletedRound = Math.max.apply(
      null,
      rounds.filter(round => round.is_finished).map(round => round.number)
    );
    const latestCompletedRoundObj = rounds.find(
      i => i.number === latestCompletedRound
    );
    let latestCompletedRoundId = 0;
    if (latestCompletedRoundObj) {
      latestCompletedRoundId = latestCompletedRoundObj.id;
    }

    if (lastSurveyId > 0 && latestCompletedRoundId > 0) {
      await this.getPeerScoreValue(latestCompletedRoundId, lastSurveyId);
    }

    const showSkillsAssessmentTab = surveys.some(survey =>
      survey.questions.some(
        question => question.category === "skills_assessment"
      )
    );

    this.setState(
      prevState => ({
        surveys,
        performance: showSkillsAssessmentTab
          ? [
            ...prevState.performance,
            { id: 3, name: "Skills Assessment", isActive: false }
          ]
          : prevState.performance
      }),
      () => {
        surveys.forEach(survey => {
          if (this.isSurveyFinished(survey)) {
            this.loadReport(survey, Auth.getUserId());
          }
        });
      }
    );
  }

  get peerScoreValue() {
    const { peerScoreValue } = this.state;

    return peerScoreValue === null
      ? '---'
      : `${(peerScoreValue * 100).toFixed(2)}%`;
  }

  async getPeerScoreValue(roundId, surveyId) {
    const userId = Number(this.getCurrentUserId());

    try {
      const res = await getRequest(
        `${process.env.REACT_APP_API_URL}v2/games/usergrade/${surveyId}/${roundId}/${userId}/`
      );
      const { peer_adjustment_grade } = res.body;

      this.setState({
        peerScoreValue: Number(peer_adjustment_grade),
      });
    } catch (err) {
      this.setState({
        peerScoreValue: null,
      });
      console.log('Cannot get peer score data', err);
    }
  }

  get gradingValue() {
    const { userGrade, teamGrade } = this.state;
    const curved_adjusted_cumulative_value = get(
      userGrade,
      "curved_adjusted_cumulative_value",
      0
    );
    const adjusted_cumulative_value = get(
      userGrade,
      "adjusted_cumulative_value",
      0
    );

    let result = 0;
    if (curved_adjusted_cumulative_value > 0) {
      result = curved_adjusted_cumulative_value;
    } else if (adjusted_cumulative_value > 0) {
      result = adjusted_cumulative_value;
    } else if (teamGrade > 0) {
      result = teamGrade;
    }

    return result !== 0 ? `${(result * 100).toFixed(2)}%` : "-----";
  }

  isSurveyFinished = survey => {
    const surveyEndDate = moment(survey.end);
    const currentDate = moment.utc();

    return currentDate.isAfter(surveyEndDate);
  };

  loadReport = async ({ id: survey_id, stage }, receiver_id) => {
    if (survey_id && Number(survey_id) > 0) {
      try {
        const { body: report } = await getRequest(
          `${process.env.REACT_APP_API_URL}v2/forms/score/${receiver_id}/${survey_id}/`
        );
        const { reports } = this.state;

        this.setState({
          reports: {
            ...reports,
            [stage]: report
          }
        });
      } catch (e) {
        // do nothing
      }
    }
  };

  handleShowTab = id => {
    const performance = this.state.performance.map(tab =>
      tab.id === id ? { ...tab, isActive: true } : { ...tab, isActive: false }
    );

    this.setState({
      performance
    });
  };

  get latestCompletedFormValue() {
    const { reports } = this.state;
    let key = "";

    if ("initial" in reports) {
      key = "initial";
    }

    if ("second" in reports) {
      key = "second";
    }

    if ("final" in reports) {
      key = "final";
    }

    return key === ""
      ? "---"
      : _.get(reports[key], "average_user_score", 0).toFixed(2);
  }

  get latestCompletedFormKey() {
    const { reports } = this.state;
    let key = "";

    if ("initial" in reports) {
      key = "initial";
    }

    if ("second" in reports) {
      key = "second";
    }

    if ("final" in reports) {
      key = "final";
    }

    return key;
  }

  getGradingValue = (number, is_finished) => {
    const { grading } = this.state;

    if (is_finished === true && number in grading) {
      return `${(grading[number] * 100).toFixed(2)}%`;
    }

    return "-----";
  };

  get peerAdjustment() {
    const { currentScore } = this.state;
    const average_user_score = get(currentScore, "average_user_score", 0);
    const team_median_score = get(currentScore, "team_median_score", 0);
    const result =
      average_user_score < 6
        ? team_median_score !== 0
          ? Math.min(1, average_user_score / Math.max(team_median_score, 6))
          : 0
        : 1;

    return result !== 0 ? `${(result * 100).toFixed(2)}%` : "-----";
  }

  get individualGrade() {
    const { userGrade } = this.state;
    const result = get(userGrade, "adjusted_cumulative_value", 0);

    return result !== 0 ? `${(result * 100).toFixed(2)}%` : "-----";
  }

  get finalGrade() {
    const { userGrade } = this.state;
    const result = get(userGrade, "curved_adjusted_cumulative_value", 0);

    return result !== 0 ? `${(result * 100).toFixed(2)}%` : "-----";
  }

  skillsAssessmentDescription = (showSkillsAssessmentData) => {
    return showSkillsAssessmentData === true ? 'view results' : '---';
  }

  skillsAssessmentStrengthsCategories = (key) => {
    const { skillsAssessmentRating } = this.state;

    if (key === '') {
      return [];
    }

    if (key === 'initial') {
      return skillsAssessmentRating.length > 0
        ? Object.entries(skillsAssessmentRating[0]).filter(i => i[1] >= 0.7).map(i => i[0])
        : [];
    }

    if (key === 'second') {
      return skillsAssessmentRating.length > 1
        ? Object.entries(skillsAssessmentRating[1]).filter(i => i[1] >= 0.7).map(i => i[0])
        : [];
    }

    return skillsAssessmentRating.length > 2
      ? Object.entries(skillsAssessmentRating[2]).filter(i => i[1] >= 0.7).map(i => i[0])
      : [];
  }

  skillsAssessmentAreasOfDevelopmentCategories = (key) => {
    const { skillsAssessmentRating } = this.state;

    if (key === '') {
      return [];
    }

    if (key === 'initial') {
      return skillsAssessmentRating.length > 0
        ? Object.entries(skillsAssessmentRating[0]).filter(i => i[1] <= 0.3).map(i => i[0])
        : [];
    }

    if (key === 'second') {
      return skillsAssessmentRating.length > 1
        ? Object.entries(skillsAssessmentRating[1]).filter(i => i[1] <= 0.3).map(i => i[0])
        : [];
    }

    return skillsAssessmentRating.length > 2
      ? Object.entries(skillsAssessmentRating[2]).filter(i => i[1] <= 0.3).map(i => i[0])
      : [];
  }

  sortSurveys = surveys => {
    // Stage-first ordering: Initial peer eval on the left, Final on
    // the right. Anything else (unknown stage) sorts last with a
    // stable index tiebreak so relative order is preserved.
    const stageOrder = { initial: 0, final: 1 };
    return (Array.isArray(surveys) ? surveys : [])
      .map((s, i) => ({ s, i }))
      .sort((a, b) => {
        const aStage = String((a.s && a.s.stage) || '').toLowerCase();
        const bStage = String((b.s && b.s.stage) || '').toLowerCase();
        const ao = stageOrder[aStage] != null ? stageOrder[aStage] : 2;
        const bo = stageOrder[bStage] != null ? stageOrder[bStage] : 2;
        if (ao !== bo) return ao - bo;
        return a.i - b.i;
      })
      .map(x => x.s);
  };

  isSurveyEnded = survey => {
    const endDate = moment(survey.end);
    const currentDate = moment.utc();

    return currentDate.isSameOrAfter(endDate);
  };

  getCurrentUserId = () => {
    return Auth.getUserId();
  };

  skillsAssessmentChartData = (showSkillsAssessmentData) => {
    const { skillsAssessmentRating } = this.state;
    const result = [];
    let hasSecond = false;
    let hasFinal = false;
    const defaultData = {
      Organized: 0.7,
      Communicative: 0.75,
      Creative: 0.57,
      Inquisitive: 0.3,
      Accountable: 0.48,
      Analytical: 0.38,
      Decisive: 0.63,
      Supportive: 0.9,
    };

    if (skillsAssessmentRating && skillsAssessmentRating.length > 0) {
      const categories = [];
      for (const data of skillsAssessmentRating) {
        for (const category of Object.keys(data).filter(c => c.toLowerCase() !== 'not applicable')) {
          if (!categories.includes(category)) {
            categories.push(category);
            const value = {
              category,
              initial: showSkillsAssessmentData ? get(skillsAssessmentRating, `0.${category}`, 0) : get(defaultData, category, 0),
              second: get(skillsAssessmentRating, `1.${category}`, 0),
              final: get(skillsAssessmentRating, `2.${category}`, 0),
            };
            result.push(value);

            if (value.second > 0) {
              hasSecond = true;
            }

            if (value.final > 0) {
              hasFinal = true;
            }
          }
        }
      }
    }

    return {
      hasSecond,
      hasFinal,
      data: result,
    };
  }

  customRadiusTick = (showSkillsAssessmentData) => ({ radius, stroke, x, y, textAnchor, payload }) => {
    const { skillsAssessmentTooltip } = this.state;
    const radiusOffset = 10;
    const radiusCorrection = 90;
    const xCalculated = radiusOffset * Math.sin(Math.PI * 2 * (payload.coordinate - radiusCorrection) / 360);
    const yCalculated = radiusOffset * Math.cos(Math.PI * 2 * (payload.coordinate - radiusCorrection) / 360);
    const rectCorrection = Math.abs(payload.coordinate - 90);
    let rectOffsetX = 32;
    let rectOffsetY = 10;
    if (rectCorrection > 0 && rectCorrection < 180) {
      rectOffsetX = 0;
    }

    if (rectCorrection > 180) {
      rectOffsetX = 64;
    }

    return (
      <g className="recharts-layer recharts-polar-angle-axis-tick">
        {!showSkillsAssessmentData && (
          <rect
            x={x - xCalculated - rectOffsetX}
            y={y - yCalculated - rectOffsetY}
            rx="5"
            ry="5"
            width="64"
            height="9"
            style={{ fill: '#c4c4c4' }}>
          </rect>
        )}
        {showSkillsAssessmentData && (
          <text
            radius={radius}
            stroke={stroke}
            x={x - xCalculated}
            y={y - yCalculated}
            className={cn("recharts-text recharts-polar-angle-axis-tick-value", {
              'recharts-polar-angle-axis-tick-value-selected': skillsAssessmentTooltip && payload.value === skillsAssessmentTooltip.category,
            })}
            textAnchor={textAnchor}
          >
            <tspan x={x - xCalculated} dy="0em">{payload.value}</tspan>
          </text>
        )}
      </g>
    );
  }

  customTickClick = (showSkillsAssessmentData) => (data) => {
    const {
      skillsAssessmentDefinitions,
      skillsAssessmentTooltip,
    } = this.state;

    if (
      showSkillsAssessmentData === true
      && data
      && data.value
      && skillsAssessmentDefinitions[data.value]
    ) {
      if (skillsAssessmentTooltip && skillsAssessmentTooltip.category === data.value) {
        this.setState({
          skillsAssessmentTooltip: null,
        });
      } else {
        this.setState({
          skillsAssessmentTooltip: {
            category: data.value,
            text: skillsAssessmentDefinitions[data.value],
          },
        });
      }
    }
  }

  render() {
    const {
      performance,
      surveys,
      reports,
      skillsAssessmentCalculated,
      skillsAssessmentTooltip,
      showPerformanceSummary,
      userScores,
      selectedSurveyId,
    } = this.state;

    // Peer-rating card data derived from the fetched user-score
    // responses. We surface BOTH open and closed peer-eval surveys
    // so the Initial/Final toggle always renders when both exist —
    // an "evaluation still open" message replaces the score/insight
    // rendering downstream when the toggled survey hasn't closed.
    const initialEval = surveys.find(
      s => s && s.stage === 'initial'
    );
    const finalEval = surveys.find(
      s => s && s.stage === 'final'
    );
    const initialClosed = initialEval && this.isSurveyFinished(initialEval);
    const finalClosed = finalEval && this.isSurveyFinished(finalEval);
    // "Not open yet" = survey exists but its `start` timestamp is in
    // the future. Distinct from "open" (started, not yet closed) and
    // "closed" (past end).
    const isSurveyNotOpenYet = (survey) => {
      if (!survey || !survey.start) return false;
      return new Date(survey.start).getTime() > Date.now();
    };
    const initialNotOpenYet = initialEval && !initialClosed && isSurveyNotOpenYet(initialEval);
    const finalNotOpenYet = finalEval && !finalClosed && isSurveyNotOpenYet(finalEval);
    const initialScore = initialEval ? userScores[initialEval.id] : null;
    const finalScore = finalEval ? userScores[finalEval.id] : null;
    const hasBothEvals = !!(initialEval && finalEval);
    // Fall back to whichever survey exists if state hasn't picked one
    // yet (fetchUserScores only sets selectedSurveyId when a finished
    // survey has scores; the toggle still needs a default before then).
    const effectiveSelectedSurveyId =
      selectedSurveyId
      || (initialEval && initialEval.id)
      || (finalEval && finalEval.id)
      || null;
    const selectedIsFinal = finalEval && effectiveSelectedSurveyId === finalEval.id;
    const activeScore = selectedIsFinal
      ? finalScore
      : (initialScore || finalScore);
    const activeSurveyClosed = selectedIsFinal ? finalClosed : initialClosed;
    const activeSurveyNotOpenYet = selectedIsFinal ? finalNotOpenYet : initialNotOpenYet;
    const fmt = (n) => (n == null || isNaN(Number(n)))
      ? '—'
      : Number(n).toFixed(1);
    // Delta only meaningful when we have BOTH scores and viewing Final.
    let deltaChip = null;
    if (
      hasBothEvals
      && selectedIsFinal
      && initialScore && finalScore
      && initialScore.average_user_score != null
      && finalScore.average_user_score != null
    ) {
      const delta = Number(finalScore.average_user_score) - Number(initialScore.average_user_score);
      const rounded = Math.abs(delta).toFixed(1);
      if (Number(rounded) > 0) {
        deltaChip = delta > 0
          ? `Up ${rounded} since initial`
          : `Down ${rounded} since initial`;
      }
    }
    // Insight for the selected survey drives the teammates-said card
    // + feedback modal. Missing = professor hasn't released yet
    // (only meaningful once the survey has closed).
    const { insights } = this.state;
    const activeInsight = effectiveSelectedSurveyId ? insights[effectiveSelectedSurveyId] : null;
    const insightJson = activeInsight && activeInsight.insight_json;
    const overallSummary = insightJson && insightJson.overall_summary;
    const hasInsights = !!insightJson;

    const isPeerRatingScoreActive = _.get(
      _.find(performance, {
        name: "Peer Rating"
      }),
      "isActive",
      false
    );

    const isSkillsAssessmentActive = _.get(
      _.find(performance, {
        name: "Skills Assessment"
      }),
      "isActive",
      false
    );

    const sortedSurveys = this.sortSurveys(surveys);
    const latestCompletedFormValue = this.latestCompletedFormValue;
    const latestCompletedFormKey = this.latestCompletedFormKey;
    const showSkillsAssessmentData = skillsAssessmentCalculated === true && ['initial', 'second', 'final'].includes(latestCompletedFormKey);
    const showSkillsAssessmentDataSecond = skillsAssessmentCalculated === true && ['second', 'final'].includes(latestCompletedFormKey);
    const showSkillsAssessmentDataFinal = skillsAssessmentCalculated === true && latestCompletedFormKey === 'final';
    const skillsAssessmentStrengthsCategories = this.skillsAssessmentStrengthsCategories(latestCompletedFormKey);
    const skillsAssessmentAreasOfDevelopmentCategories = this.skillsAssessmentAreasOfDevelopmentCategories(latestCompletedFormKey);
    const skillsAssessmentChartData = this.skillsAssessmentChartData(showSkillsAssessmentData);



    return (
      <Profile.Wrapper className="instructorPage instructorDashboard">
        <Profile.CardWrapper>
          {sortedSurveys.map(survey => (
            <PeerEvaluation key={survey.id} survey={survey} />
          ))}
        </Profile.CardWrapper>
        {
          showPerformanceSummary &&
          <Summary.Grid>
            <Summary.Card>
              <Summary.TitleRow>
                <Summary.TitleRowTitle>Your peer rating</Summary.TitleRowTitle>
                {hasBothEvals && (
                  <Summary.Toggle>
                    <Summary.ToggleButton
                      type="button"
                      active={!selectedIsFinal}
                      onClick={() => this.selectSurveyById(initialEval.id)}
                    >
                      Initial
                    </Summary.ToggleButton>
                    <Summary.ToggleButton
                      type="button"
                      active={selectedIsFinal}
                      onClick={() => this.selectSurveyById(finalEval.id)}
                    >
                      Final
                    </Summary.ToggleButton>
                  </Summary.Toggle>
                )}
              </Summary.TitleRow>
              {activeSurveyClosed ? (
                <Fragment>
                  <Summary.RatingLabel>
                    {selectedIsFinal ? 'Final rating' : 'Initial rating'}
                  </Summary.RatingLabel>
                  <Summary.RatingValueRow>
                    <Summary.RatingValue>
                      {fmt(activeScore && activeScore.average_user_score)}
                    </Summary.RatingValue>
                    <Summary.RatingDenom>/ 5</Summary.RatingDenom>
                  </Summary.RatingValueRow>
                  {deltaChip && (
                    <Summary.DeltaChip>{deltaChip}</Summary.DeltaChip>
                  )}
                  <Summary.BreakdownRow>
                    <Summary.BreakdownLabel dotColor="#2AB3AF">You</Summary.BreakdownLabel>
                    <Summary.BreakdownValue>
                      {fmt(activeScore && activeScore.average_user_score)}
                    </Summary.BreakdownValue>
                  </Summary.BreakdownRow>
                  <Summary.BreakdownRow>
                    <Summary.BreakdownLabel dotColor="#e5e5e5">Team average</Summary.BreakdownLabel>
                    <Summary.BreakdownValue>
                      {fmt(activeScore && activeScore.team_average_score)}
                    </Summary.BreakdownValue>
                  </Summary.BreakdownRow>
                  <Summary.BreakdownRow>
                    <Summary.BreakdownLabel dotColor="#c4c4c4">Team median</Summary.BreakdownLabel>
                    <Summary.BreakdownValue>
                      {fmt(activeScore && activeScore.team_median_score)}
                    </Summary.BreakdownValue>
                  </Summary.BreakdownRow>
                </Fragment>
              ) : (
                <Summary.Blurb>
                  {activeSurveyNotOpenYet
                    ? "This evaluation hasn't opened yet. Your peer rating will appear here after it opens and closes."
                    : "This evaluation is still open. Your peer rating will appear here after it closes."}
                </Summary.Blurb>
              )}
            </Summary.Card>
            <Summary.Card>
              <Summary.TitleRow>
                <Summary.TitleRowTitle>What your teammates said</Summary.TitleRowTitle>
                {hasBothEvals && (
                  <Summary.Toggle>
                    <Summary.ToggleButton
                      type="button"
                      active={!selectedIsFinal}
                      onClick={() => this.selectSurveyById(initialEval.id)}
                    >
                      Initial
                    </Summary.ToggleButton>
                    <Summary.ToggleButton
                      type="button"
                      active={selectedIsFinal}
                      onClick={() => this.selectSurveyById(finalEval.id)}
                    >
                      Final
                    </Summary.ToggleButton>
                  </Summary.Toggle>
                )}
              </Summary.TitleRow>
              <Summary.Blurb>
                {activeSurveyNotOpenYet
                  ? "This evaluation hasn't opened yet. Your teammates' feedback will appear here after it opens and closes."
                  : !activeSurveyClosed
                    ? "This evaluation is still open. Your teammates' feedback will appear here after it closes."
                    : hasInsights
                      ? overallSummary
                      : "Your professor hasn't released feedback for this evaluation yet. Check back after it's shared."}
              </Summary.Blurb>
              <Summary.FeedbackButton
                type="button"
                onClick={this.openFeedbackModal}
                disabled={!hasInsights || !activeSurveyClosed}
              >
                View feedback
              </Summary.FeedbackButton>
            </Summary.Card>
          </Summary.Grid>
        }
        {
          !showPerformanceSummary &&
          <LockScreen.Wrapper>
            <img alt="The results is locked" src={IconLock} />
            <LockScreen.Text>To unlock results, complete peer evaluation.</LockScreen.Text>
          </LockScreen.Wrapper>
        }
        {this.state.feedbackModalOpen && this.renderFeedbackModal()}
      </Profile.Wrapper>
    );
  }

  // Feedback modal body pulls evidence from /v2/insights/mine/<id>/
  // for whichever survey is currently selected outside the modal
  // (selectedSurveyId, shared with the card toggles). Modal tabs also
  // update selectedSurveyId so the card + modal stay in sync.
  renderFeedbackModal = () => {
    const { surveys, insights, selectedSurveyId } = this.state;
    const initialEval = surveys.find(
      s => s && s.stage === 'initial' && this.isSurveyFinished(s)
    );
    const finalEval = surveys.find(
      s => s && s.stage === 'final' && this.isSurveyFinished(s)
    );
    const hasBothEvals = !!(initialEval && finalEval);
    const selectedIsFinal = finalEval && selectedSurveyId === finalEval.id;

    const activeInsight = selectedSurveyId ? insights[selectedSurveyId] : null;
    const insightJson = activeInsight && activeInsight.insight_json;
    // Flatten every evidence string across the skills lists.
    const collect = (section) => {
      if (!insightJson || !insightJson[section]) return [];
      const skills = insightJson[section].skills || [];
      const out = [];
      skills.forEach(s => {
        (s.evidence || []).forEach(e => {
          if (e) out.push(e);
        });
      });
      return out;
    };
    const appreciate = collect('strengths');
    const grow = collect('weaknesses');

    return (
      <Feedback.Backdrop onClick={this.closeFeedbackModal}>
        <Feedback.Card onClick={e => e.stopPropagation()}>
          <Feedback.Header>
            <Feedback.HeaderText>
              <Feedback.Title>What your teammates said</Feedback.Title>
              <Feedback.Subtitle>
                Anonymous written feedback collected with each evaluation.
              </Feedback.Subtitle>
            </Feedback.HeaderText>
            <Feedback.Close
              type="button"
              onClick={this.closeFeedbackModal}
              aria-label="Close"
            >
              ×
            </Feedback.Close>
          </Feedback.Header>
          {hasBothEvals && (
            <Feedback.TabRow>
              <Feedback.Tab
                type="button"
                active={selectedIsFinal}
                onClick={() => this.selectSurveyById(finalEval.id)}
              >
                Final evaluation
              </Feedback.Tab>
              <Feedback.Tab
                type="button"
                active={!selectedIsFinal}
                onClick={() => this.selectSurveyById(initialEval.id)}
              >
                Initial evaluation
              </Feedback.Tab>
            </Feedback.TabRow>
          )}
          <Feedback.Body>
            <div>
              <Feedback.ColumnHeader>
                <Feedback.ColumnIcon background="hsla(151, 55%, 92%, 1)" color="hsl(151, 60%, 30%)">
                  👍
                </Feedback.ColumnIcon>
                <Feedback.ColumnLabel>
                  <Feedback.ColumnTitle>What they appreciate</Feedback.ColumnTitle>
                  <Feedback.ColumnSubtitle>Strengths teammates called out</Feedback.ColumnSubtitle>
                </Feedback.ColumnLabel>
              </Feedback.ColumnHeader>
              {appreciate.length > 0
                ? appreciate.map((text, i) => (
                  <Feedback.QuoteCard key={`appreciate-${i}`} accent="#2AB37A">
                    <Feedback.QuoteText>{text}</Feedback.QuoteText>
                  </Feedback.QuoteCard>
                ))
                : (
                  <Feedback.QuoteText>
                    No strengths feedback yet.
                  </Feedback.QuoteText>
                )}
            </div>
            <div>
              <Feedback.ColumnHeader>
                <Feedback.ColumnIcon background="hsla(30, 100%, 92%, 1)" color="hsl(30, 90%, 40%)">
                  ↑
                </Feedback.ColumnIcon>
                <Feedback.ColumnLabel>
                  <Feedback.ColumnTitle>Room to grow</Feedback.ColumnTitle>
                  <Feedback.ColumnSubtitle>What they'd like to see more or less of</Feedback.ColumnSubtitle>
                </Feedback.ColumnLabel>
              </Feedback.ColumnHeader>
              {grow.length > 0
                ? grow.map((text, i) => (
                  <Feedback.QuoteCard key={`grow-${i}`} accent="#F4A93C">
                    <Feedback.QuoteText>{text}</Feedback.QuoteText>
                  </Feedback.QuoteCard>
                ))
                : (
                  <Feedback.QuoteText>
                    No growth feedback yet.
                  </Feedback.QuoteText>
                )}
            </div>
          </Feedback.Body>
        </Feedback.Card>
      </Feedback.Backdrop>
    );
  };
}


// WEBPACK FOOTER //
// ./src/components/peer-review/index.js